import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { Form, Formik } from 'formik';
import { usePlacesWidget } from 'react-google-autocomplete';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { updateApplication } from '../../../actions/applications';
import {
  asyncActions,
  clearErrors,
  handleAsyncError,
} from '../../../actions/general';
import {
  authenticate,
  getRoute,
  getSession,
  loading,
} from '../../../actions/sessions';
import { validateBirthDate, validatePhone } from '../../../actions/users';
import useModal from '../../../hooks/useModal';
import useRouteStatus from '../../../hooks/useRouteStatus';
import useSessionEntity from '../../../hooks/useSessionEntity';
import useValidateRoute from '../../../hooks/useValidateRoute';
import {
  emojiRegexTest,
  googleAddressValidationApiKey,
  poBoxRegex,
  states,
} from '../../../lib/constants/SiteVariables';
import {
  addressFromPlace,
  validateAddress,
} from '../../../lib/utils/address_validation';
import {
  addressValidation,
  getEmailValidationError,
  validateEmailBasic,
  validateEmailWithBackend,
} from '../../../lib/utils/validations';
import ApplicationRecord from '../../../records/application';
import PrimaryButton from '../../shared/buttons/PrimaryButton';
import Error from '../../shared/Error';
import ColumnInput from '../../shared/inputs/ColumnInput';
import EmailErrorComponent from '../../shared/inputs/EmailErrorComponent';
import StickyInput from '../../shared/inputs/StickyInput';
import StickySelect from '../../shared/inputs/StickySelect';
import Loading from '../../shared/Loading';
import { AddressMatchModal, ModalWrapper } from '../../shared/modal';
import AddressUnconfirmedModal from '../../shared/modal/AddressUnconfirmedModal';
import AddressValidationModal from '../../shared/modal/AddressValidationModal';
import ProgressBars from '../../shared/ProgressBars';

const ROLE_OWNER = 'owner';
const ROLE_REPRESENTATIVE = 'representative';
const SKIP_CONFIRMATION_MODAL_WIDTH = 320;
const SKIP_CONFIRMATION_MODAL_MARGIN = 16;
const OWNER_SENSITIVE_INFORMATION_FIELDS = [
  'birthdate',
  'ssn',
];

const normalizeEmailValidation = (validation) =>
  validation?.result || validation || { valid: true };

export default function ApplicantDetails({ location }) {
  const dispatch = useDispatch();
  const history = useHistory();
  const emailValidationCache = useRef({});
  const addressConfirmedRef = useRef(false);
  const forceAddressRef = useRef(false);
  const refreshTimeoutRef = useRef(null);
  const skipLinkRef = useRef(null);
  const formRef = useRef();
  const applicantTypeOnEntryRef = useRef(null);
  const isLoading = useSelector((state) => state.sessions.get('loading'));
  const application = useSessionEntity('application');
  const asyncErrors = useSelector((state) => state.general.get('errors'));
  const { isModalActive, toggleModal } = useModal();
  const [foundUser, setFoundUser] = useState(false);
  const [ctaDisabled, setCtaDisabled] = useState(false);
  const [forceAddress, setForceAddress] = useState(false);
  const [addressConfirmed, setAddressConfirmed] = useState(false);
  const [validatedAddress, setValidatedAddress] = useState(null);
  const [previousResponseId, setPreviousResponseId] = useState(null);
  const [skipConfirmationPosition, setSkipConfirmationPosition] = useState({});
  const [showSkipConfirmation, setShowSkipConfirmation] = useState(false);
  const [ownerPersonalInformationSkipped, setOwnerPersonalInformationSkipped] =
    useState(() => Boolean(application?.owner_personal_information_skipped));
  const confirmModal = useModal();
  const validationModal = useModal();
  const copyAddressToModel = (address) => {
    const values = { ...formRef.current.values };
    values.personal_address.address1 = address.address1;
    values.personal_address.address2 = address.address2;
    values.personal_address.city = address.city;
    values.personal_address.state = address.state;
    values.personal_address.zip = address.zip;
    formRef.current.setValues(values);
  };
  const addressAutoPop = usePlacesWidget({
    apiKey: googleAddressValidationApiKey,
    onPlaceSelected: (place) => {
      if (!place.address_components) return;

      copyAddressToModel(addressFromPlace(place));
    },
    options: {
      types: ['address'],
      componentRestrictions: { country: 'us' },
    },
  });

  // Keep this step's layout stable while save/auth/session/route calls finish.
  // The update response may reclassify the applicant before navigation occurs.
  if (!applicantTypeOnEntryRef.current && application?.applicant_type) {
    applicantTypeOnEntryRef.current = application.applicant_type;
  }

  const applicantTypeForStep =
    applicantTypeOnEntryRef.current || application?.applicant_type;
  const isEnterprise = applicantTypeForStep === 'enterprise';
  const showSkipLink =
    application?.has_enterprise_gar_signal &&
    applicantTypeForStep === 'smb';
  const supportsOwnerPersonalInformation = applicantTypeForStep === 'smb';
  const ownerFieldsDisabled = ownerPersonalInformationSkipped;

  const setOwnerForceAddress = (value) => {
    forceAddressRef.current = value;
    setForceAddress(value);
  };

  const confirmOwnerAddress = () => {
    addressConfirmedRef.current = true;
    setAddressConfirmed(true);
  };

  const resetOwnerAddressValidation = () => {
    addressConfirmedRef.current = false;
    forceAddressRef.current = false;
    setAddressConfirmed(false);
    setForceAddress(false);
    setValidatedAddress(null);
  };

  const handleEmailBlur = async (email, setFieldError, setFieldTouched) => {
    if (!email) return;
    setFieldTouched('email', true);

    const lowerEmail = email.toLowerCase();
    const cached = emailValidationCache.current[lowerEmail];
    let validation = cached;
    if (!cached) {
      validation = normalizeEmailValidation(
        await validateEmailWithBackend(lowerEmail, dispatch)
      );
      emailValidationCache.current[lowerEmail] = validation;
    }

    if (!validation.valid) {
      const error = getEmailValidationError(lowerEmail, validation);
      setFieldError('email', error);
    }
  };

  const handleEmailSuggestionClick = (
    suggestedEmail,
    setFieldValue,
    setFieldError
  ) => {
    setFieldValue('email', suggestedEmail);
    setFieldError('email', undefined);
  };

  const checkPhone = async (val) => {
    try {
      const res = await dispatch(validatePhone(val));
      return res.result;
    } catch (e) {
      dispatch(handleAsyncError(e));
    }
  };

  const checkBirthdate = async (val) => {
    try {
      const res = await dispatch(validateBirthDate(val));
      return res.result;
    } catch (e) {
      dispatch(handleAsyncError(e));
    }
  };

  useRouteStatus(location);

  useEffect(() => {
    if (foundUser) history.push('/init/existing_user');
  }, [foundUser, history]);

  useEffect(() => {
    if (asyncErrors.count() > 0) {
      const errors = asyncErrors.toJS();

      if (
        errors.find(
          (e) =>
            e &&
            (e.includes('Phone number already in use') ||
              e.includes('Email address already in use'))
        )
      ) {
        setFoundUser(true);
        dispatch(loading(false));
      }

      if (errors.find((e) => e && e.includes('try refreshing'))) {
        dispatch(loading(true));
        setCtaDisabled(true);

        refreshTimeoutRef.current = setTimeout(() => {
          dispatch(getSession({ cart: false, prequal: false, merchant: false }))
            .then(() => dispatch(authenticate()))
            .then(() => dispatch(getRoute()))
            .then(() => dispatch(clearErrors()))
            .finally(() => dispatch(loading(false)));
        }, 1000);
      }
    }

    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
        refreshTimeoutRef.current = null;
      }
    };
  }, [asyncErrors, dispatch]);

  const save = (
    values,
    isAuthorizedSigner,
    skipped = false,
    usesEnterprisePhonePayload = false
  ) => {
    const representativePhone =
      values.contact_phone_number || values.phone || values.business_phone;
    const enterprisePhone =
      values.business_phone ||
      (values.applicant_role === ROLE_REPRESENTATIVE
        ? representativePhone
        : values.phone);

    dispatch(loading(true));

    return dispatch(
      asyncActions(
        updateApplication(
          new ApplicationRecord({
            ...values,
            ...(values.applicant_role === ROLE_REPRESENTATIVE
              ? {
                  contact_phone_number: representativePhone,
                  phone: usesEnterprisePhonePayload
                    ? enterprisePhone
                    : representativePhone,
                }
              : usesEnterprisePhonePayload
                ? {
                    contact_phone_number: values.phone,
                    phone: enterprisePhone,
                  }
                : {}),
            is_authorized_signer: isAuthorizedSigner,
            owner_personal_information_skipped: skipped,
          })
        ),
        authenticate(),
        getSession({ cart: false, prequal: false, merchant: false }),
        getRoute()
      )
    ).then((res) => {
      dispatch(loading(false));
      return res;
    });
  };

  const submitValidatedValues = async (values, setFieldError) => {
    try {
      dispatch(loading(true));

      const lowerEmail = values.email.toLowerCase();
      let emailValidation = emailValidationCache.current[lowerEmail];
      if (!emailValidation) {
        emailValidation = normalizeEmailValidation(
          await validateEmailWithBackend(lowerEmail, dispatch)
        );
        emailValidationCache.current[lowerEmail] = emailValidation;
      }

      if (!emailValidation.valid) {
        setFieldError(
          'email',
          getEmailValidationError(lowerEmail, emailValidation)
        );
      }

      const shouldValidateOwnerPersonalInformation =
        supportsOwnerPersonalInformation &&
        values.applicant_role === ROLE_OWNER &&
        !ownerPersonalInformationSkipped;
      let addressesMatch = false;
      let validated = null;
      let ownerAddressValid = true;
      let birthdateCheck = { valid: true };

      if (shouldValidateOwnerPersonalInformation) {
        addressesMatch = addressValidation(
          application?.get('business_address').toJS(),
          values?.personal_address
        );

        if (addressesMatch) {
          if (!forceAddressRef.current && !forceAddress) toggleModal();

          values.personal_address.validation_response = JSON.stringify({
            result:
              'Validation skipped due to personal and business addresses being the same',
          });
        } else {
          validated = validatedAddress
            ? validatedAddress
            : await validateAddress(
                values.personal_address,
                null,
                previousResponseId
              );
          setValidatedAddress(validated);
          values.personal_address.validation_response = JSON.stringify(
            validated.response
          );

          if (!previousResponseId) {
            setPreviousResponseId(validated.response.responseId);
          }

          const unconfirmed = validated.address.unconfirmedComponents.length;
          const suggestedChange = validated.address.diff.length;

          if (
            unconfirmed &&
            !addressConfirmedRef.current &&
            !addressConfirmed
          ) {
            confirmModal.toggleModal();
          }

          if (
            !unconfirmed &&
            suggestedChange &&
            !addressConfirmedRef.current &&
            !addressConfirmed
          ) {
            validationModal.toggleModal();
          }
        }

        birthdateCheck = await checkBirthdate(values.birthdate);

        if (!birthdateCheck?.valid) {
          setFieldError(
            'birthdate',
            `* ${birthdateCheck?.error || 'Please enter a valid date'}`
          );
        }

        ownerAddressValid =
          (addressesMatch && (forceAddressRef.current || forceAddress)) ||
          (!addressesMatch &&
            ((!validated?.address?.unconfirmedComponents?.length &&
              !validated?.address?.diff?.length) ||
              addressConfirmedRef.current ||
              addressConfirmed));
      }

      const phoneFieldName =
        values.applicant_role === ROLE_REPRESENTATIVE
          ? 'contact_phone_number'
          : 'phone';
      const phoneCheck = await checkPhone(values[phoneFieldName]);
      if (!phoneCheck?.valid) {
        setFieldError(phoneFieldName, '* Please enter a valid phone number');
      }

      if (
        emailValidation.valid &&
        phoneCheck?.valid &&
        birthdateCheck?.valid &&
        ownerAddressValid
      ) {
        const isAuthorizedSigner = values.applicant_role === ROLE_OWNER;
        const submittedOwnerSkip =
          isAuthorizedSigner && ownerPersonalInformationSkipped;
        const usesEnterprisePhonePayload =
          values.applicant_type === 'enterprise' ||
          (values.applicant_type === 'smb' &&
            values.has_enterprise_gar_signal &&
            (!isAuthorizedSigner || submittedOwnerSkip));

        save(
          values,
          isAuthorizedSigner,
          submittedOwnerSkip,
          usesEnterprisePhonePayload
        );
      } else {
        dispatch(loading(false));
      }
    } catch (error) {
      dispatch(handleAsyncError(error));
      dispatch(loading(false));
    }
  };

  if (!useValidateRoute({ apply: true })) return <></>;

  if (!application)
    return <Loading className="w-14 h-14" containerClass="my-24 sm:my-0" />;

  return (
    <div role="main" className="mx-6 sm:mx-auto">
      <ProgressBars />
      <div className="mt-4 mb-1 sm:mb-4">
        <div className="text-xl sm:text-4xl font-extrabold">
          Who's applying?
        </div>
      </div>
      <Error />

      <Formik
        innerRef={formRef}
        initialValues={{
          ...application.toJS(),
          applicant_role: isEnterprise ? ROLE_REPRESENTATIVE : ROLE_OWNER,
        }}
        validate={async (values) => {
          const errors = {};
          const addNested = (field, msg) =>
            (errors.personal_address = {
              ...errors.personal_address,
              [field]: msg,
            });

          Object.keys(values).forEach((key) => {
            const value = values[key];

            if (typeof value === 'string' && emojiRegexTest.test(value)) {
              errors[key] = '* Emojis are not accepted.';
            }
          });

          if (!values.first_name || values.first_name.trim().length < 2) {
            errors.first_name = '* Required';
          }
          if (!values.last_name || values.last_name.trim().length < 2) {
            errors.last_name = '* Required';
          }
          if (!values.email) {
            errors.email = '* Required';
          } else if (!validateEmailBasic(values.email.toLowerCase())) {
            errors.email = '* Invalid email address';
          } else {
            const email = values.email.toLowerCase();
            const validation = emailValidationCache.current[email];
            if (validation && !validation.valid) {
              errors.email = getEmailValidationError(email, validation);
            }
          }

          const phoneFieldName =
            values.applicant_role === ROLE_REPRESENTATIVE
              ? 'contact_phone_number'
              : 'phone';
          const phoneValue = values[phoneFieldName];

          if (!phoneValue) {
            errors[phoneFieldName] = '* Required';
          } else {
            const digitsOnly = phoneValue.replace(/[^\d]/g, '');
            if (digitsOnly.length < 10) {
              errors[phoneFieldName] = '* Please enter a valid phone number';
            }
          }

          if (values.applicant_role === ROLE_REPRESENTATIVE && values.extension) {
            const trimmedExtension = values.extension.trim();
            if (trimmedExtension && !/^\d+$/.test(trimmedExtension)) {
              errors.extension = '* Extension must contain only numbers';
            }
          }

          if (
            supportsOwnerPersonalInformation &&
            values.applicant_role === ROLE_OWNER &&
            !ownerPersonalInformationSkipped
          ) {
            if (!values.birthdate) {
              errors.birthdate = '* Required';
            } else if (values.birthdate.replace(/[^\d]/g, '').length < 8) {
              errors.birthdate = '* Please enter a valid date';
            }

            if (!values.ssn) {
              errors.ssn = '* Required';
            } else if (values.ssn.replace(/[^\d]/g, '').length < 9) {
              errors.ssn = '* Must be 9 characters in length';
            }

            if (
              !values.personal_address?.address1 ||
              values.personal_address.address1.trim().length < 2
            ) {
              addNested('address1', '* Required');
            } else if (
              poBoxRegex.test(values.personal_address.address1.trim())
            ) {
              addNested('address1', '* P.O. Boxes are not accepted.');
            }

            if (emojiRegexTest.test(values.personal_address?.address1?.trim())) {
              addNested('address1', '* Emojis are not accepted.');
            }

            if (
              values.personal_address?.address2 &&
              poBoxRegex.test(values.personal_address.address2.trim())
            ) {
              addNested('address2', '* P.O. Boxes are not accepted.');
            }

            if (emojiRegexTest.test(values.personal_address?.address2?.trim())) {
              addNested('address2', '* Emojis are not accepted.');
            }

            if (
              !values.personal_address?.city ||
              values.personal_address.city.trim().length < 2
            ) {
              addNested('city', '* Required');
            }

            if (emojiRegexTest.test(values.personal_address?.city?.trim())) {
              addNested('city', '* Emojis are not accepted.');
            }

            if (!values.personal_address?.state) {
              addNested('state', '* Required');
            }

            if (!values.personal_address?.zip) {
              addNested('zip', '* Required');
            } else if (values.personal_address.zip.trim().length < 5) {
              addNested('zip', '* Must be 5 characters');
            }
          }

          return errors;
        }}
        onSubmit={(values, { setFieldError }) =>
          submitValidatedValues(values, setFieldError)
        }
      >
        {({
          errors,
          setFieldError,
          setFieldTouched,
          setFieldValue,
          setValues,
          touched,
          values,
        }) => {
          const clearOwnerSensitiveInformationFields = () => {
            OWNER_SENSITIVE_INFORMATION_FIELDS.forEach((field) => {
              setFieldValue(field, '', false);
              setFieldTouched(field, false, false);
              setFieldError(field, undefined);
            });
          };

          const resetPersonalAddressToInitialApplicationValue = () => {
            const personalAddress = application
              ?.get('personal_address')
              ?.toJS();

            setFieldValue('personal_address', personalAddress, false);
            setFieldTouched('personal_address', false, false);
            setFieldError('personal_address', undefined);
          };

          const setSkipConfirmationAnchorPosition = () => {
            const rect = skipLinkRef.current?.getBoundingClientRect();
            if (!rect) return;

            const width = Math.min(
              SKIP_CONFIRMATION_MODAL_WIDTH,
              window.innerWidth - SKIP_CONFIRMATION_MODAL_MARGIN * 2
            );
            const left = Math.min(
              Math.max(SKIP_CONFIRMATION_MODAL_MARGIN, rect.right - width),
              window.innerWidth - width - SKIP_CONFIRMATION_MODAL_MARGIN
            );

            setSkipConfirmationPosition({
              left,
              top: rect.bottom + 8,
              width,
            });
          };

          const handleOwnerInformationLinkClick = () => {
            if (showSkipConfirmation) {
              setShowSkipConfirmation(false);
              return;
            }

            if (ownerPersonalInformationSkipped) {
              resetOwnerAddressValidation();
              setOwnerPersonalInformationSkipped(false);
              return;
            }

            setSkipConfirmationAnchorPosition();
            setShowSkipConfirmation(true);
          };

          const handleConfirmSkipOwnerInformation = () => {
            clearOwnerSensitiveInformationFields();
            resetPersonalAddressToInitialApplicationValue();
            resetOwnerAddressValidation();
            setOwnerPersonalInformationSkipped(true);
            setShowSkipConfirmation(false);
          };

          const setApplicantRole = (nextRole) => {
            const transitioningToRepresentative =
              nextRole === ROLE_REPRESENTATIVE &&
              values.applicant_role !== ROLE_REPRESENTATIVE;
            const transitioningToOwner =
              nextRole === ROLE_OWNER &&
              values.applicant_role === ROLE_REPRESENTATIVE;

            // Carry the entered number into the other role's empty phone
            // field, but never overwrite a number the applicant already has.
            setValues({
              ...values,
              applicant_role: nextRole,
              ...(transitioningToRepresentative &&
              values.phone &&
              !values.contact_phone_number
                ? { contact_phone_number: values.phone }
                : transitioningToOwner &&
                    values.contact_phone_number &&
                    !values.phone
                  ? { phone: values.contact_phone_number }
                  : {}),
            });
          };

          const showPatriotActNotice =
            supportsOwnerPersonalInformation &&
            values.applicant_role === ROLE_OWNER;

          return (
            <Form role="form" className="mx-auto">
              <div className="flex flex-col mb-4">
                <p className="block text-sm font-medium text-gray-700 mb-2">
                  What is your role?
                </p>
                <div className="flex flex-row gap-6">
                  {!isEnterprise && (
                    <div
                      className="flex items-center group cursor-pointer"
                      onClick={() => setApplicantRole(ROLE_OWNER)}
                    >
                      <input
                        type="radio"
                        className="h-4 w-4 sm:h-5 sm:w-5 border-tertiary-gray group-focus:ring-primary-blue mt-1"
                        id="applicant_role_owner"
                        name="applicant_role"
                        onChange={() => setApplicantRole(ROLE_OWNER)}
                        value={ROLE_OWNER}
                        checked={values.applicant_role === ROLE_OWNER}
                      />
                      <label
                        htmlFor="applicant_role_owner"
                        className="ml-2 block text-sm sm:text-lg font-light tracking-wide cursor-pointer"
                        onClick={() => setApplicantRole(ROLE_OWNER)}
                      >
                        I'm an owner or authorized signer
                      </label>
                    </div>
                  )}
                  <div
                    className="flex items-center group cursor-pointer"
                    onClick={() => setApplicantRole(ROLE_REPRESENTATIVE)}
                  >
                    <input
                      type="radio"
                      className="h-4 w-4 sm:h-5 sm:w-5 border-tertiary-gray group-focus:ring-primary-blue mt-1"
                      id="applicant_role_representative"
                      name="applicant_role"
                      onChange={() => setApplicantRole(ROLE_REPRESENTATIVE)}
                      value={ROLE_REPRESENTATIVE}
                      checked={values.applicant_role === ROLE_REPRESENTATIVE}
                    />
                    <label
                      htmlFor="applicant_role_representative"
                      className="ml-2 block text-sm sm:text-lg font-light tracking-wide cursor-pointer"
                      onClick={() => setApplicantRole(ROLE_REPRESENTATIVE)}
                    >
                      I'm a representative
                    </label>
                  </div>
                </div>
              </div>

              <ColumnInput
                error={{
                  first_name: errors?.first_name,
                  last_name: errors?.last_name,
                }}
                touched={{
                  first_name: touched?.first_name,
                  last_name: touched?.last_name,
                }}
                className="flex flex-col mobile:flex-row justify-between mobile:gap-x-1"
              >
                <StickyInput
                  id="first_name"
                  className="mobile:w-1/2 w-full my-1 mobile:my-0"
                  placeholder="Your First Name"
                  values={values}
                />

                <StickyInput
                  id="last_name"
                  className="mobile:w-1/2 w-full my-1 mobile:my-0"
                  placeholder="Your Last Name"
                  values={values}
                />
              </ColumnInput>

              {values.applicant_role === ROLE_OWNER ? (
                <ColumnInput
                  error={{
                    email: errors?.email,
                    phone: errors?.phone,
                  }}
                  touched={{
                    email: touched?.email,
                    phone: touched?.phone,
                  }}
                  className="flex flex-col mobile:flex-row justify-between mobile:gap-x-1 items-start"
                >
                  <StickyInput
                    id="email"
                    type="email"
                    placeholder="Your Email"
                    className="mobile:w-1/2 w-full mb-2 mobile:my-0"
                    values={values}
                    onBlur={(e) =>
                      handleEmailBlur(
                        e.target.value,
                        setFieldError,
                        setFieldTouched
                      )
                    }
                    errorComponent={EmailErrorComponent}
                    onSuggestionClick={(suggestedEmail) =>
                      handleEmailSuggestionClick(
                        suggestedEmail,
                        setFieldValue,
                        setFieldError
                      )
                    }
                  />
                  <StickyInput
                    id="phone"
                    type="tel"
                    mask="(999) 999-9999"
                    placeholder="Your Mobile Phone"
                    helperText="We'll send a verification code by text."
                    className="mobile:w-1/2 w-full mb-2 mobile:my-0"
                    values={values}
                  />
                </ColumnInput>
              ) : (
                <>
                  <ColumnInput error={errors?.email} touched={touched?.email}>
                    <StickyInput
                      id="email"
                      type="email"
                      placeholder="Your Email"
                      values={values}
                      onBlur={(e) =>
                        handleEmailBlur(
                          e.target.value,
                          setFieldError,
                          setFieldTouched
                        )
                      }
                      errorComponent={EmailErrorComponent}
                      onSuggestionClick={(suggestedEmail) =>
                        handleEmailSuggestionClick(
                          suggestedEmail,
                          setFieldValue,
                          setFieldError
                        )
                      }
                    />
                  </ColumnInput>
                  <ColumnInput
                    error={{
                      contact_phone_number: errors?.contact_phone_number,
                      extension: errors?.extension,
                    }}
                    touched={{
                      contact_phone_number: touched?.contact_phone_number,
                      extension: touched?.extension,
                    }}
                    className="flex flex-col mobile:flex-row justify-between mobile:gap-x-1 items-start"
                  >
                    <StickyInput
                      id="contact_phone_number"
                      type="tel"
                      mask="(999) 999-9999"
                      placeholder="Your Phone"
                      className="mobile:w-[70%] w-full mb-2 mobile:my-0"
                      values={values}
                    />
                    <StickyInput
                      id="extension"
                      type="tel"
                      inputMode="numeric"
                      placeholder="Ext."
                      className="mobile:w-[28%] w-full mb-2 mobile:my-0"
                      values={values}
                    />
                  </ColumnInput>
                </>
              )}

              <ColumnInput error={errors.title} touched={touched.title}>
                <StickyInput
                  id="title"
                  placeholder="Your Title"
                  values={values}
                />
              </ColumnInput>

              {supportsOwnerPersonalInformation &&
                values.applicant_role === ROLE_OWNER && (
                <div
                  data-testid="owner-personal-information"
                  className={classNames(
                    'border border-primary-border-gray rounded-lg p-4 mt-6 mb-4'
                  )}
                >
                  <div
                    data-testid="owner-personal-information-header"
                    className="flex items-center justify-between gap-4 mb-4"
                  >
                    <div className="font-semibold text-sm sm:text-base">
                      Owner Personal Information
                    </div>

                    {showSkipLink && (
                      <button
                        data-testid="owner-information-skip-link"
                        ref={skipLinkRef}
                        type="button"
                        onClick={handleOwnerInformationLinkClick}
                        className="text-sm text-primary-blue hover:text-primary-blue-hover"
                      >
                        {ownerPersonalInformationSkipped
                          ? 'Add owner information'
                          : 'Skip owner information'}
                      </button>
                    )}
                  </div>

                  <ModalWrapper
                    contentContainerClassName="px-2 xxs:px-4 py-0"
                    contentTestId="skip-owner-confirmation-content"
                    isModalActive={showSkipConfirmation}
                    overlayClassName="fixed inset-0 bg-transparent transition-opacity"
                    overlayTestId="skip-owner-confirmation-overlay"
                    panelClassName="absolute bg-white rounded-lg py-5 text-left shadow-xl transform transition-all"
                    panelStyle={skipConfirmationPosition}
                    panelTestId="skip-owner-confirmation-panel"
                    showCloseButton={false}
                    toggleModal={() => setShowSkipConfirmation(false)}
                  >
                    <div data-testid="skip-owner-confirmation" className="px-2">
                      <p className="text-xs sm:text-sm text-secondary-gray leading-5">
                        If you skip this step, your approval will take up to{' '}
                        <strong className="font-semibold text-primary">
                          2 business days
                        </strong>{' '}
                        for a decision.
                      </p>
                      <div className="mt-4 flex gap-3">
                        <button
                          type="button"
                          onClick={() => setShowSkipConfirmation(false)}
                          className="w-1/2 py-2 px-3 text-xs font-semibold rounded-lg border border-primary-border-gray text-secondary-gray hover:border-secondary-gray"
                        >
                          Go back
                        </button>
                        <button
                          type="button"
                          onClick={handleConfirmSkipOwnerInformation}
                          className="w-1/2 py-2 px-3 text-xs font-semibold rounded-lg bg-primary-blue text-white hover:bg-primary-blue-hover"
                        >
                          Skip anyway
                        </button>
                      </div>
                    </div>
                  </ModalWrapper>

                  <div
                    className={classNames(ownerFieldsDisabled && 'opacity-60')}
                  >
                    <ColumnInput
                      error={{
                        birthdate: errors?.birthdate,
                        ssn: errors?.ssn,
                      }}
                      touched={{
                        birthdate: touched?.birthdate,
                        ssn: touched?.ssn,
                      }}
                      className="flex flex-col mobile:flex-row justify-between mobile:gap-x-1"
                    >
                      <StickyInput
                        id="birthdate"
                        type="tel"
                        placeholder="Date of Birth"
                        inputMode="numeric"
                        mask="99/99/9999"
                        className="mobile:w-1/2 w-full my-1 mobile:my-0"
                        disabled={ownerFieldsDisabled}
                        values={values}
                      />
                      <StickyInput
                        id="ssn"
                        posthogMask
                        type="tel"
                        placeholder="SSN"
                        inputMode="numeric"
                        mask="999-99-9999"
                        className="mobile:w-1/2 w-full my-1 mobile:my-0"
                        disabled={ownerFieldsDisabled}
                        values={values}
                      />
                    </ColumnInput>

                    <ColumnInput
                      error={{
                        address1: errors?.personal_address?.address1,
                        address2: errors?.personal_address?.address2,
                      }}
                      touched={{
                        address1:
                          touched?.personal_address?.address1 || undefined,
                        address2:
                          touched?.personal_address?.address2 || undefined,
                      }}
                      className="flex flex-col mobile:flex-row justify-between mobile:gap-x-1"
                    >
                      <StickyInput
                        inputRef={addressAutoPop?.ref}
                        id="personal_address.address1"
                        placeholder="Personal Street Address"
                        className="mobile:w-4/5 w-full mb-2 mobile:my-0"
                        disabled={ownerFieldsDisabled}
                        values={values}
                        onChange={(e) => {
                          setFieldValue(
                            'personal_address.address1',
                            e.target.value
                          );
                          resetOwnerAddressValidation();
                        }}
                      />
                      <StickyInput
                        id="personal_address.address2"
                        placeholder="Suite or Floor"
                        className="mobile:w-1/5 w-full mobile:my-0"
                        disabled={ownerFieldsDisabled}
                        values={values}
                        onChange={(e) => {
                          setFieldValue(
                            'personal_address.address2',
                            e.target.value
                          );
                          resetOwnerAddressValidation();
                        }}
                      />
                    </ColumnInput>

                    <ColumnInput
                      error={{
                        city: errors?.personal_address?.city,
                        state: errors?.personal_address?.state,
                        zip: errors?.personal_address?.zip,
                      }}
                      touched={{
                        city: touched?.personal_address?.city,
                        state: touched?.personal_address?.state,
                        zip: touched?.personal_address?.zip,
                      }}
                      className="flex flex-col mobile:flex-row justify-between mobile:gap-x-1"
                    >
                      <StickyInput
                        id="personal_address.city"
                        className="mobile:w-3/5 w-full mb-2 mobile:my-0"
                        placeholder="City"
                        disabled={ownerFieldsDisabled}
                        values={values}
                        onChange={(e) => {
                          setFieldValue(
                            'personal_address.city',
                            e.target.value
                          );
                          resetOwnerAddressValidation();
                        }}
                      />
                      <StickySelect
                        id="personal_address.state"
                        className="mobile:w-1/5 w-full mb-2 mobile:my-0"
                        placeholder="State"
                        options={states}
                        shortOptionsDisplay={true}
                        disabled={ownerFieldsDisabled}
                        values={values}
                        onChange={(e) => {
                          setFieldValue(
                            'personal_address.state',
                            e.target.value
                          );
                          resetOwnerAddressValidation();
                        }}
                      />
                      <StickyInput
                        id="personal_address.zip"
                        className="mobile:my-0"
                        mask="99999"
                        type="tel"
                        placeholder="Zip"
                        disabled={ownerFieldsDisabled}
                        values={values}
                        onChange={(e) => {
                          setFieldValue('personal_address.zip', e.target.value);
                          resetOwnerAddressValidation();
                        }}
                      />
                    </ColumnInput>
                  </div>
                </div>
              )}

              {showPatriotActNotice && (
                <p className="text-xs mt-4 mb-2 text-secondary-gray">
                  The USA Patriot Act requires all financial institutions to
                  obtain, verify and record information that identifies every
                  customer. Completion of this documentation is required in
                  order to comply with the USA Patriot Act.
                </p>
              )}
              <p className="text-xs mt-4 mb-2 text-secondary-gray">
                By clicking continue, I agree to the
                <a
                  href="https://www.creditkey.com/legal/terms-of-service-and-electronic-disclosures"
                  target="_new" rel="noopener noreferrer"
                  className="mx-1 text-primary-blue hover:text-primary-blue-hover underline"
                >
                  Credit Key Terms of Service & Electronic Disclosures
                </a>
                and
                <a
                  href="https://www.creditkey.com/legal/privacy-policy"
                  target="_new" rel="noopener noreferrer"
                  className="mx-1 text-primary-blue hover:text-primary-blue-hover underline"
                >
                  Privacy Policy
                </a>
                .
              </p>
              <PrimaryButton
                buttonText="Continue"
                buttonColor="bg-primary-blue"
                hoverClass="hover:bg-primary-blue-hover"
                disabled={isLoading || ctaDisabled}
                isLoading={isLoading}
              />
              <AddressMatchModal
                businessAddress={application?.get('business_address').toJS()}
                handleSubmit={() =>
                  submitValidatedValues(values, setFieldError)
                }
                isLoading={isLoading}
                isModalActive={isModalActive}
                personalAddress={values?.personal_address}
                setForceAddress={setOwnerForceAddress}
                toggleModal={toggleModal}
              />
              <AddressUnconfirmedModal
                address={validatedAddress?.address}
                confirmAddress={confirmOwnerAddress}
                handleSubmit={() =>
                  submitValidatedValues(values, setFieldError)
                }
                isLoading={isLoading}
                isModalActive={confirmModal.isModalActive}
                toggleModal={confirmModal.toggleModal}
              />
              <AddressValidationModal
                address={values?.personal_address}
                confirmAddress={confirmOwnerAddress}
                copyAddressToModel={copyAddressToModel}
                handleSubmit={() =>
                  submitValidatedValues(values, setFieldError)
                }
                isLoading={isLoading}
                isModalActive={validationModal.isModalActive}
                toggleModal={validationModal.toggleModal}
                validatedAddress={validatedAddress?.address}
              />
            </Form>
          );
        }}
      </Formik>
    </div>
  );
}
